import { useContext, useState } from 'react';
import {
    Box,
    Button,
    ClickAwayListener,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    Typography
} from '@mui/material';
import {
    ArrowBackIos,
    AttachMoney,
    CalendarToday,
    CheckBox,
    CheckBoxOutlineBlank,
    Circle,
    Close,
    CreditCard,
    RadioButtonChecked,
    RadioButtonUnchecked,
    Refresh
} from '@mui/icons-material';
import { DatePicker } from 'formik-mui-lab';
import { Formik, Form, Field } from 'formik';
import { LocalizationProvider } from '@mui/lab';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CARD_FORM_STEP } from '../RequestCard';
// Utils and Helpers
import { CARD_TYPE, REPLENISH_FRECUENCY } from 'helpers/constants';
import { hasCreditLine, hasCreditModel } from 'helpers/businesses';
import { numberFormat } from 'helpers/i18n';
import dayjs from 'helpers/dayjs';
// Assets
import { ReactComponent as ReplenishDailyIcon } from 'assets/createCards/replenishDailyV2.svg';
import { ReactComponent as ReplenishOneTimeIcon } from 'assets/createCards/replenishOneTimeV3.svg';
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';

const CustomizeCard = ({ card, cardholder, onBackBtnClick, onStepSubmit, onCardModalClose }) => {
    const { businessInfo } = useContext(AuthContext);
    const [isAutolockChecked, setIsAutolockChecked] = useState(false);
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

    const validationSchema = Yup.object().shape({
        limitAmount: Yup.number().positive('Invalid amount').min(1, 'It must be higher than $1.00').required('Required'),
        autoLock: Yup.date()
            .typeError('Invalid date')
            .nullable()
            .test('autoLock', 'Invalid date', autoLock => {
                if (!autoLock) return true;
                if (dayjs(autoLock).isSame(card?.autoLock)) return true;
                if (dayjs(autoLock).isAfter(dayjs())) return true;
                return false;
            })
            .optional(),
        has_manual_replenish: Yup.boolean()
    });

    const handleReplenishClick = (replenishType, setFieldValue) => {
        if (card.type === CARD_TYPE.PHYSICAL && replenishType === REPLENISH_FRECUENCY.ONE_TIME) {
            return;
        }
        setFieldValue('has_manual_replenish', replenishType);
    };

    const handleAutolockCheckboxClick = () => {
        setIsAutolockChecked(isAutolockChecked => !isAutolockChecked);
    };

    return (
        <Box className="request-card" sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Box className="request-card__modal-title">
                        {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                            ? 'Customize your card'
                            : 'Set the card funding amount'}
                    </Box>
                    <Box className="request-card__modal-paginator">
                        <span className="request-card__modal-page-indicator">2/3 Steps</span>
                        <Circle className="request-card__indicator-active" />
                        <Circle className="request-card__indicator-active" />
                        <Circle className="request-card__indicator-inactive" />
                    </Box>
                </Box>

                <Box alignSelf="start">
                    <IconButton onClick={onCardModalClose}>
                        <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                    </IconButton>
                </Box>
            </Box>

            <Formik initialValues={card} enableReinitialize={true} validationSchema={validationSchema} onSubmit={onStepSubmit}>
                {({ setFieldValue, values }) => (
                    <Grid container columnSpacing={{ md: 3 }}>
                        <Grid item md={6}>
                            <Form id="customize-card-form">
                                <Box display="flex" justifyContent="space-between" sx={{ marginBottom: '12px' }}>
                                    <FormControl fullWidth>
                                        <FormLabel required className="request-card__card-input-label">
                                            {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                                                ? 'Choose the card limit'
                                                : 'Funding Amount'}
                                        </FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            placeholder="Enter amount"
                                            name="limitAmount"
                                            type="text"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoney sx={{ color: 'black' }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box>
                                    {hasCreditModel(businessInfo) && hasCreditLine(businessInfo) && (
                                        <>
                                            <FormLabel required className="request-card__card-input-label">
                                                Choose the replenishment type
                                            </FormLabel>
                                            <Box
                                                className={`
                                                        request-card__replenish-type${' '}
                                                        ${
                                                            values.has_manual_replenish === REPLENISH_FRECUENCY.ONE_TIME &&
                                                            'request-card__replenish-type-checked'
                                                        }${' '}
                                                        ${
                                                            card.type === CARD_TYPE.PHYSICAL &&
                                                            'request-card__replenish-type-disabled'
                                                        }
                                                    `}
                                                onClick={() => handleReplenishClick(REPLENISH_FRECUENCY.ONE_TIME, setFieldValue)}>
                                                <Box display="flex">
                                                    {values.has_manual_replenish === REPLENISH_FRECUENCY.ONE_TIME ? (
                                                        <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                                    ) : (
                                                        <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                                    )}
                                                    <Box>
                                                        <Typography component="div" className="request-card__replenish-name">
                                                            Manual
                                                        </Typography>
                                                        <Typography
                                                            component="div"
                                                            className="request-card__replenish-description">
                                                            Spend up to ${values.limitAmount} and replenish manually.
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box alignSelf="center">
                                                    <ReplenishOneTimeIcon />
                                                </Box>
                                            </Box>
                                            <Box
                                                className={`
                                                        request-card__replenish-type${' '}
                                                        ${
                                                            values.has_manual_replenish === REPLENISH_FRECUENCY.DAILY &&
                                                            'request-card__replenish-type-checked'
                                                        }
                                                    `}
                                                onClick={() => handleReplenishClick(REPLENISH_FRECUENCY.DAILY, setFieldValue)}>
                                                <Box display="flex">
                                                    {values.has_manual_replenish === REPLENISH_FRECUENCY.DAILY ? (
                                                        <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                                    ) : (
                                                        <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                                    )}
                                                    <Box>
                                                        <Typography component="div" className="request-card__replenish-name">
                                                            Automatic
                                                        </Typography>
                                                        <Typography
                                                            component="div"
                                                            className="request-card__replenish-description">
                                                            Spend up to ${values.limitAmount} and auto-replenish every 4-6 days.
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box alignSelf="center">
                                                    <ReplenishDailyIcon />
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                    <Box display="flex">
                                        {isAutolockChecked ? (
                                            <CheckBox
                                                sx={{ color: '#1BABE2', marginRight: '12px', cursor: 'pointer' }}
                                                onClick={handleAutolockCheckboxClick}
                                            />
                                        ) : (
                                            <CheckBoxOutlineBlank
                                                sx={{ color: '#909DA6', marginRight: '12px', cursor: 'pointer' }}
                                                onClick={handleAutolockCheckboxClick}
                                            />
                                        )}
                                        <Box>
                                            <Typography component="div" className="request-card__replenish-name">
                                                Add Auto Lock Date
                                            </Typography>
                                            <Typography component="div" className="request-card__replenish-description">
                                                Use Auto-lock date to freeze cards after a certain date.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {isAutolockChecked && (
                                        <ClickAwayListener onClickAway={() => setIsDatepickerOpen(false)}>
                                            <FormControl fullWidth sx={{ marginTop: '8px' }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <Field
                                                        component={DatePicker}
                                                        variant="outlined"
                                                        name="autoLock"
                                                        mask="__/__/____"
                                                        minDate={dayjs().add(1, 'day')}
                                                        open={isDatepickerOpen}
                                                        onAccept={() => setIsDatepickerOpen(false)}
                                                        textField={{
                                                            size: 'small',
                                                            onClick: () =>
                                                                setIsDatepickerOpen(isDatepickerOpen => !isDatepickerOpen)
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </ClickAwayListener>
                                    )}
                                </Box>
                            </Form>
                        </Grid>
                        <Grid item md={6}>
                            <Box className="request-card__card-banner-container">
                                <Box className="request-card__card-image-container" position="relative">
                                    <img
                                        className="request-card__card-image"
                                        src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                        alt="virtual card"
                                    />
                                    <Typography component="div" className="request-card__card-secret request-card__card-number">
                                        **** **** **** ****
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="request-card__card-secret request-card__card-expiration">
                                        ****
                                    </Typography>
                                    <Typography component="div" className="request-card__card-secret request-card__card-cvv">
                                        ***
                                    </Typography>

                                    <Typography
                                        component="div"
                                        className="request-card__card-backside-cardholder-name embossing-ellipsis"
                                        sx={{
                                            color: `${
                                                card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {`${cardholder?.first_name} ${cardholder?.last_name}`}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="request-card__card-backside-cardholder-company embossing-ellipsis"
                                        sx={{
                                            color: `${
                                                card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {businessInfo.legal_name}
                                    </Typography>
                                </Box>
                                <Box className="request-card__card-banner-benefits">
                                    <Box>
                                        <Box display="flex" alignItems="center" marginBottom="22px">
                                            <CreditCard sx={{ marginRight: '14px' }} />
                                            <Typography component="span" className="request-card__card-banner-benefit">
                                                {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                                                    ? `Limit: ${numberFormat(values.limitAmount)}`
                                                    : `Card Balance: ${numberFormat(values.limitAmount)}`}
                                            </Typography>
                                        </Box>
                                        {hasCreditModel(businessInfo) && hasCreditLine(businessInfo) && (
                                            <Box display="flex" alignItems="center" marginBottom="22px">
                                                <Refresh sx={{ marginRight: '14px' }} />
                                                <Typography component="span" className="request-card__card-banner-benefit">
                                                    Replenishment:{' '}
                                                    {values.has_manual_replenish === REPLENISH_FRECUENCY.ONE_TIME
                                                        ? 'Manual'
                                                        : 'Automatic'}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box display="flex" alignItems="center">
                                            <CalendarToday sx={{ marginRight: '14px' }} />
                                            <Typography component="span" className="request-card__card-banner-benefit">
                                                Lock Date: {values.autoLock ? dayjs(values.autoLock).format('DD/MM/YYYY') : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Formik>
            <Box display="flex" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                <Button variant="outlined" onClick={() => onBackBtnClick(CARD_FORM_STEP.TYPE)}>
                    <ArrowBackIos fontSize="small" />
                </Button>
                <Button variant="contained" type="submit" form="customize-card-form">
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

export default CustomizeCard;
